import React, { memo, useContext, useMemo } from 'react'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { MODEL_SOURCES } from 'Pods/ThreeDStudio/Services/Constants/modelSources'
import { trackSettingsInteraction } from 'Pods/ThreeDStudio/Services/studioTracker'
import ToggleSelector from 'Pods/ThreeDStudio/Shared/ToggleSelector/ToggleSelector'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setExtraQuality } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

const handleExtraQualityToggle = (extraQuality) => {
  trackSettingsInteraction('ultra_mode', extraQuality)
  setExtraQuality(extraQuality)
}

// The toggle stays enabled for every engine: turning it on narrows the eligible
// schemas (via their active_if) so the store switches to an engine that supports
// ultra_mode, the same way smart low poly does. The row is only hidden for actions
// where no model offers it at all, since there would be nothing to switch to.
const actionSupportsUltraMode = (generationAction) => MODEL_SOURCES.some((schema) => (
  schema.action === generationAction && schema.input_schema?.properties?.ultra_mode !== undefined
))

function ExtraQualitySelector() {
  const t = useTranslation()
  const { enableExtraQuality, extractingCoreStyles } = useContext(ThreeDStudioPageContext)
  const generationAction = useThreeDStudioStore(state => state.schema.generationAction)
  const isAvailable = useMemo(() => actionSupportsUltraMode(generationAction), [generationAction])
  // Rendered during critical-CSS extraction even with the flag off, so the row's
  // styles make it into the generated stylesheet before the flag is turned on.
  const isVisible = (enableExtraQuality || extractingCoreStyles) && isAvailable

  const trailing = useMemo(() => (
    <span className="extra-quality-selector">
      <span className="extra-quality-selector__badge">
        <span className="extra-quality-selector__badge-text cgt-overline-sm">
          {t('leftSide.extraQuality.badge')}
        </span>
      </span>
      <span className="extra-quality-selector__hint cgt-caption">
        {t('leftSide.extraQuality.hint')}
      </span>
    </span>
  ), [t])

  if (!isVisible) {
    return null
  }

  return (
    <ToggleSelector
      id="model-settings-extra-quality"
      keyName="extraQuality"
      label={t('leftSide.extraQuality.label')}
      setFunction={handleExtraQualityToggle}
      sliceName="modelSettings"
      trailing={trailing}
    />
  )
}

export default memo(ExtraQualitySelector)
